import React, { useEffect, useState } from "react";
import axios from "axios";
import { Star, ExternalLink } from "lucide-react";
import { testimonials, reviewSummary } from "../mock";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const REVIEWS_LINK =
  "https://search.google.com/local/reviews?placeid=ChIJy8CG8h6RZJYRY0S1U3PhgDY";

const Stars = ({ n }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star
        key={i}
        size={15}
        className={i <= n ? "fill-amber-400 text-amber-400" : "text-slate-300"}
      />
    ))}
  </div>
);

const GoogleG = () => (
  <svg width="18" height="18" viewBox="0 0 48 48">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);

export default function Testimonials() {
  const [rating, setRating] = useState(reviewSummary.rating);
  const [total, setTotal] = useState(reviewSummary.count);
  const [reviews, setReviews] = useState(testimonials);

  useEffect(() => {
    let active = true;
    axios
      .get(`${BACKEND_URL}/api/reviews`)
      .then((res) => {
        if (!active || !res.data) return;
        const withText = (res.data.reviews || []).filter(
          (r) => r.text && r.text.trim().length > 0
        );
        if (res.data.rating) setRating(res.data.rating);
        if (res.data.total) setTotal(res.data.total);
        if (withText.length > 0) setReviews(withText);
      })
      .catch(() => {
        /* mantiene los testimonios locales como respaldo */
      });
    return () => {
      active = false;
    };
  }, []);

  return (
    <section id="opiniones" className="bg-[#f6f8fc]">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-28">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 reveal">
          <div className="max-w-2xl">
            <span className="text-xs font-semibold tracking-[0.2em] uppercase text-[#1d6fc0]">
              Opiniones
            </span>
            <h2 className="font-display font-extrabold text-3xl lg:text-[2.6rem] leading-tight mt-4 text-[#0b2545]">
              La confianza de nuestros pacientes
            </h2>
          </div>
          <div className="flex items-center gap-4 bg-white rounded-2xl border border-slate-200 px-5 py-4 shadow-sm">
            <GoogleG />
            <div>
              <div className="flex items-center gap-2">
                <span className="font-display font-extrabold text-2xl text-[#0b2545]">
                  {Number(rating).toFixed(1)}
                </span>
                <Stars n={Math.round(rating)} />
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                Basado en {total} reseñas de Google
              </div>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
          {reviews.map((t, i) => (
            <div
              key={i}
              className="reveal card-hover rounded-2xl bg-white border border-slate-200 p-6"
              style={{ transitionDelay: `${(i % 3) * 70}ms` }}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {t.photo ? (
                    <img
                      src={t.photo}
                      alt={t.name}
                      referrerPolicy="no-referrer"
                      className="w-11 h-11 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-11 h-11 rounded-full bg-[#1d6fc0] text-white grid place-items-center font-display font-bold">
                      {t.initial}
                    </div>
                  )}
                  <div>
                    <div className="font-semibold text-[#0b2545] text-sm">{t.name}</div>
                    <div className="text-xs text-slate-400">{t.date}</div>
                  </div>
                </div>
                <GoogleG />
              </div>
              <div className="mt-4">
                <Stars n={t.rating} />
              </div>
              <p className="text-sm text-slate-600 mt-3 leading-relaxed">{t.text}</p>
            </div>
          ))}
        </div>

        <div className="flex justify-center mt-10 reveal">
          <a
            href={REVIEWS_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-6 py-3 text-sm font-medium text-[#0b2545] hover:border-[#1d6fc0] hover:text-[#1d6fc0] transition-colors"
          >
            <GoogleG /> Ver todas las reseñas en Google <ExternalLink size={15} />
          </a>
        </div>
      </div>
    </section>
  );
}
